import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { historyService } from '../Services/historyService';
import consentService from '../Services/consentService';
import {
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Settings,
  Lock,
  Info,
  Trash2,
  Loader2,
  UserX,
  Eye,
  EyeOff,
  Shield,
  LogOut,
  Type,
  Download,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { useNavigate } from 'react-router-dom';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  deleteUser,
} from 'firebase/auth';
import { auth } from '../firebase';

const FUNCTIONS_URL =
  process.env.REACT_APP_FUNCTIONS_URL ||
  'https://europe-west1-medaviceia.cloudfunctions.net';

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Confidentialité
  const [saveHistory, setSaveHistory] = useState(true);
  const [loading, setLoading] = useState(false);

  // Consentement données de santé
  const [healthConsentAccepted, setHealthConsentAccepted] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);

  // Accessibilité
  const [fontSize, setFontSize] = useState('normal');

  // Sécurité du compte (mot de passe)
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Sessions
  const [sessionLoading, setSessionLoading] = useState(false);

  // Gestion des données
  const [deleting, setDeleting] = useState(false);

  // Suppression du compte
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    loadSettings();
    loadHealthConsentStatus();
  }, [user]);

  const applyFontSize = (size) => {
    const sizes = { small: '14px', normal: '16px', large: '19px' };
    document.documentElement.style.fontSize = sizes[size] || '16px';
  };

  const loadSettings = async () => {
    if (!user?.uid) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists() && userDoc.data().settings) {
        setSaveHistory(userDoc.data().settings.saveHistory ?? true);
        const savedFontSize = userDoc.data().settings.fontSize ?? 'normal';
        setFontSize(savedFontSize);
        applyFontSize(savedFontSize);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const loadHealthConsentStatus = async () => {
    if (!user?.uid) return;
    try {
      const consentDoc = await getDoc(doc(db, 'users', user.uid, 'consents', 'health_data'));
      setHealthConsentAccepted(consentDoc.exists() && consentDoc.data().accepted === true);
    } catch (error) {
      console.error('Erreur chargement consentement:', error);
    }
  };

  const handleFontSizeChange = async (size) => {
    if (!user?.uid) return;
    setFontSize(size);
    applyFontSize(size);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.fontSize': size,
      });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleToggleSaveHistory = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const newValue = !saveHistory;
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.saveHistory': newValue,
      });
      setSaveHistory(newValue);
      toast.success(newValue ? 'Historique activé' : 'Historique désactivé');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConsent = async () => {
    if (!user?.uid) return;
    if (
      !window.confirm(
        'Voulez-vous vraiment retirer votre consentement pour le traitement de vos données de santé ?\n\n' +
          "Vous ne pourrez plus utiliser l'assistant médical IA tant que vous ne l'aurez pas accepté à nouveau."
      )
    ) {
      return;
    }
    setConsentLoading(true);
    try {
      await consentService.revokeHealthDataConsent(user.uid);
      setHealthConsentAccepted(false);
      toast.success('Consentement retiré avec succès');
    } catch (error) {
      toast.error('Erreur lors du retrait du consentement');
    } finally {
      setConsentLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setPasswordLoading(true);
    try {
      const currentUser = auth.currentUser;
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Mot de passe modifié avec succès');
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        toast.error('Mot de passe actuel incorrect');
      } else {
        toast.error('Erreur lors de la modification du mot de passe');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRevokeSessions = async () => {
    if (!user?.uid) return;
    setSessionLoading(true);
    try {
      const currentUser = auth.currentUser;
      const token = await currentUser.getIdToken();
      const response = await fetch(`${FUNCTIONS_URL}/revokeUserSessions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erreur serveur');
      toast.success('Autres sessions déconnectées');
    } catch (error) {
      toast.error('Erreur lors de la révocation des sessions');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!user?.uid) {
      toast.error('Utilisateur non connecté');
      return;
    }
    if (
      !window.confirm(
        '⚠️ Supprimer TOUT votre historique de conversations ?\n\n' +
          'Cette action est IRRÉVERSIBLE.\n\n' +
          "• Toutes vos conversations avec l'IA seront supprimées\n" +
          '• Cette action ne peut pas être annulée'
      )
    ) {
      return;
    }
    setDeleting(true);
    try {
      const deletedCount = await historyService.clearAllHistory(user.uid);
      if (deletedCount > 0) {
        toast.success(`${deletedCount} conversation(s) supprimée(s)`);
      } else {
        toast.info('Aucun historique à supprimer');
      }
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }
    setDeletingAccount(true);
    try {
      const currentUser = auth.currentUser;
      // Ré-authentification OBLIGATOIRE (défense en profondeur : ne pas dépendre
      // uniquement du `disabled` du bouton). Aligne le civil sur la page pro.
      if (!password) {
        toast.error('Veuillez saisir votre mot de passe pour confirmer');
        setDeletingAccount(false);
        return;
      }
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential); // throw si mdp faux → bloquant

      // Plus d'appel cancelSubscription ici : deleteUserAccount annule désormais
      // Stripe elle-même, EN PREMIER (functions/index.js), avec gestion "pas d'abo"
      // + idempotence "déjà annulé". Pré-annuler ici recréait un double-cancel
      // dangereux (500 stripe_cancel_failed → compte non supprimable). On délègue
      // toute l'annulation au backend.
      const token = await currentUser.getIdToken();
      const response = await fetch(`${FUNCTIONS_URL}/deleteUserAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        // Seul échec "métier" désormais délégué au backend. Aucun conseil
        // d'annulation préalable : cela recréerait le double-cancel qu'on a retiré.
        if (body.error === 'stripe_cancel_failed') {
          throw new Error(
            "L'annulation de votre abonnement a échoué. Votre compte n'a PAS été " +
              "supprimé et vous n'avez rien perdu. Merci de réessayer dans quelques " +
              'instants ; si le problème persiste, contactez le support.'
          );
        }
        throw new Error(body.error || `Erreur serveur (${response.status})`);
      }
      await auth.signOut();
      toast.success('Compte supprimé avec succès');
      navigate('/');
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        toast.error('Mot de passe incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        toast.error('Veuillez vous reconnecter avant de supprimer votre compte');
      } else {
        toast.error('Erreur lors de la suppression: ' + error.message);
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const fontSizeOptions = [
    { value: 'small', label: 'Petite', preview: 'text-xs', size: '14px' },
    { value: 'normal', label: 'Normale', preview: 'text-sm', size: '16px' },
    { value: 'large', label: 'Grande', preview: 'text-base', size: '19px' },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="settings-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground dark:text-gray-100">
                Paramètres
              </h1>
              <p className="text-muted dark:text-gray-400">Gérez vos préférences</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">

          {/* Accessibilité */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-violet-100 dark:bg-violet-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Type className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-1">
                  Accessibilité
                </h3>
                <p className="text-sm text-muted dark:text-gray-400 mb-4">
                  Ajustez la taille du texte sur toute l'application
                </p>
                <div className="flex gap-3">
                  {fontSizeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleFontSizeChange(option.value)}
                      className={`flex-1 flex flex-col items-center gap-2 px-3 py-3 rounded-xl border-2 transition-all ${
                        fontSize === option.value
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
                          : 'border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:border-violet-300 dark:hover:border-violet-700'
                      }`}
                    >
                      <span style={{ fontSize: option.size }} className="font-semibold leading-none">
                        Aa
                      </span>
                      <span className="text-xs font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Confidentialité */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-accent/10 dark:bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-3">
                  Confidentialité
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <Label
                      htmlFor="save-history"
                      className="text-base font-medium cursor-pointer dark:text-gray-200"
                    >
                      Enregistrer l'historique des conversations
                    </Label>
                    <p className="text-sm text-muted dark:text-gray-400 mt-1">
                      Sauvegardez vos échanges avec l'IA pour les consulter plus tard
                    </p>
                  </div>
                  <Switch
                    id="save-history"
                    checked={saveHistory}
                    onCheckedChange={handleToggleSaveHistory}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Consentement données de santé */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-1">
                  Données de santé
                </h3>
                <p className="text-sm text-muted dark:text-gray-400 mb-4">
                  {healthConsentAccepted
                    ? "Vous avez accepté le traitement de vos données de santé par l'assistant IA. Vous pouvez retirer ce consentement à tout moment."
                    : "Vous n'avez pas (ou plus) donné votre consentement pour le traitement de vos données de santé par l'assistant IA."}
                </p>
                {healthConsentAccepted && (
                  <Button
                    onClick={handleRevokeConsent}
                    variant="outline"
                    disabled={consentLoading}
                    className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:border-amber-600 dark:text-amber-400 dark:hover:bg-amber-950/30"
                  >
                    {consentLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Retrait en cours...
                      </>
                    ) : (
                      'Retirer mon consentement données de santé'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Sécurité du compte */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-1">
                  Sécurité du compte
                </h3>
                <p className="text-sm text-muted dark:text-gray-400 mb-4">
                  Modifiez votre mot de passe
                </p>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  {/* Mot de passe actuel */}
                  <div>
                    <Label htmlFor="current-password" className="dark:text-gray-200">
                      Mot de passe actuel
                    </Label>
                    <div className="relative mt-2">
                      <Input
                        id="current-password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, currentPassword: e.target.value })
                        }
                        className="pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted dark:text-gray-400 hover:text-foreground dark:hover:text-gray-200"
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Nouveau mot de passe */}
                  <div>
                    <Label htmlFor="new-password" className="dark:text-gray-200">
                      Nouveau mot de passe
                    </Label>
                    <div className="relative mt-2">
                      <Input
                        id="new-password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, newPassword: e.target.value })
                        }
                        className="pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted dark:text-gray-400 hover:text-foreground dark:hover:text-gray-200"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted dark:text-gray-400 mt-1">Minimum 6 caractères</p>
                  </div>

                  {/* Confirmation */}
                  <div>
                    <Label htmlFor="confirm-password" className="dark:text-gray-200">
                      Confirmer le nouveau mot de passe
                    </Label>
                    <div className="relative mt-2">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                        }
                        className="pr-10 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted dark:text-gray-400 hover:text-foreground dark:hover:text-gray-200"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={passwordLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    {passwordLoading ? 'Modification...' : 'Modifier le mot de passe'}
                  </Button>
                </form>
              </div>
            </div>
          </Card>

          {/* Sessions actives */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <LogOut className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-1">
                  Sessions actives
                </h3>
                <p className="text-sm text-muted dark:text-gray-400 mb-4">
                  Déconnectez tous les autres appareils connectés à votre compte. Votre session
                  actuelle restera active.
                </p>
                <Button
                  onClick={handleRevokeSessions}
                  variant="outline"
                  disabled={sessionLoading}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-950/30"
                >
                  {sessionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Révocation...
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      Déconnecter toutes les autres sessions
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>

          {/* Gestion des données */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-3">
                  Gestion des données
                </h3>
                <div>
                  <p className="text-sm text-muted dark:text-gray-400 mb-4">
                    Supprimez définitivement toutes vos conversations avec l'IA. Cette action est
                    irréversible et vos données ne pourront pas être récupérées.
                  </p>
                  <Button
                    onClick={handleClearHistory}
                    variant="outline"
                    disabled={deleting}
                    className="border-red-500 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-950/30"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Suppression en cours...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer tout l'historique
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Mes données personnelles (portabilité RGPD — art. 20) */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100 mb-1">
                  Mes données personnelles
                </h3>
                <p className="text-sm text-muted dark:text-gray-400 mb-4">
                  Exporter une copie de vos données (format JSON).
                </p>
                <Button
                  onClick={() => navigate('/my-data')}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-950/30"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Accéder à mes données
                </Button>
              </div>
            </div>
          </Card>

          {/* Suppression du compte */}
          <Card className="p-6 bg-white dark:bg-gray-900 border-red-200 dark:border-red-900">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <UserX className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-red-600 dark:text-red-400 mb-3">
                  Supprimer mon compte
                </h3>
                <div>
                  <p className="text-sm text-muted dark:text-gray-400 mb-2">
                    Cette action est définitive et irréversible :
                  </p>
                  <ul className="text-sm text-muted dark:text-gray-400 mb-4 list-disc list-inside space-y-1">
                    <li>Votre compte et vos données de santé seront supprimés définitivement</li>
                    <li>Votre historique de conversations sera effacé</li>
                    <li>Votre abonnement premium sera annulé immédiatement (sans remboursement)</li>
                    <li>
                      Vos factures sont conservées par notre prestataire de paiement (obligation
                      légale)
                    </li>
                    <li>Vous ne pourrez pas récupérer votre compte</li>
                  </ul>
                  <Button
                    onClick={() => setShowDeleteModal(true)}
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-950/30"
                  >
                    <UserX className="w-4 h-4 mr-2" />
                    Supprimer mon compte
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* À propos */}
          <Card className="p-6 bg-gradient-to-br from-slate-50 to-white dark:from-gray-800 dark:to-gray-900 border-slate-200 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100">
                  Remadia Health
                </h3>
                <p className="text-sm text-muted dark:text-gray-400">
                  Version 2.2.0 • Assistance médicale intelligente
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Modal de confirmation suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 bg-white dark:bg-gray-900">
            <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">
              ⚠️ Supprimer votre compte
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Cette action est irréversible. Votre compte et vos données de santé seront
              définitivement supprimés. Vos factures sont conservées par notre prestataire de
              paiement pour nos obligations légales.
            </p>
            {user?.subscription_type === 'premium' && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  <strong>Attention :</strong> Votre abonnement premium sera annulé immédiatement
                  sans remboursement.
                </p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium dark:text-gray-200">Mot de passe actuel</Label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Entrez votre mot de passe"
                />
              </div>
              <div>
                <Label className="text-sm font-medium dark:text-gray-200">
                  Tapez <strong className="text-red-600">SUPPRIMER</strong> pour confirmer
                </Label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="SUPPRIMER"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPassword('');
                  setConfirmText('');
                }}
                variant="outline"
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                disabled={deletingAccount}
              >
                Annuler
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || confirmText !== 'SUPPRIMER' || !password}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {deletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  'Supprimer définitivement'
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SettingsPage;
